import { SyncPhaseDto } from 'src/app/services/dtos'
import { timeAgoString } from 'src/app/shared/utils'
import { AppStatusState, ProcessStatus } from '../process-state/process-state.component'
import { RelevantStatus } from 'src/app/shared/types'
import { RaisedIssue } from '../../services/issue'

export const appStatusVals = {
  awaitInitialRp: [AppStatusState.INDETERMINATE, 'Awaiting initial recovery point creation', RelevantStatus.STAGING],
  awaitInitialSync: [
    AppStatusState.INDETERMINATE,
    'Awaiting initial sync to recovery environment',
    RelevantStatus.SYNC,
  ],
  awaitTest: [AppStatusState.INDETERMINATE, 'Awaiting test failover to recovery environment', RelevantStatus.SYNC],
  awaitFailover: [AppStatusState.INDETERMINATE, 'Awaiting failover to recovery environment', RelevantStatus.SYNC],
  captureInitialRp: [AppStatusState.INDETERMINATE, 'Capturing initial recovery point', RelevantStatus.STAGING],
  captureRp: [AppStatusState.INDETERMINATE, 'Capturing new recovery point', RelevantStatus.STAGING],
  captureRpFailed: [AppStatusState.ERROR, 'Capturing recovery point failed', RelevantStatus.STAGING],
  failedOver: [AppStatusState.SUCCESS, 'Currently failed over to recovery environment', RelevantStatus.SYNC],
  testFailedOverSuccessfully: [
    AppStatusState.SUCCESS,
    'Currently testing failover to recovery environment',
    RelevantStatus.SYNC,
  ],
  failoverFailed: [AppStatusState.ERROR, 'Failover to recovery environment failed', RelevantStatus.SYNC],
  failingOver: [AppStatusState.INDETERMINATE, 'Performing failover to recovery environment', RelevantStatus.SYNC],
  initialSync: [AppStatusState.INDETERMINATE, 'Performing initial sync to recovery environment', RelevantStatus.SYNC],
  testing: [AppStatusState.INDETERMINATE, 'Performing test failover to recovery environment', RelevantStatus.SYNC],
  applyingRp: [AppStatusState.INDETERMINATE, 'Applying recovery point from {time_ago}', RelevantStatus.SYNC],
  rpStatus: [AppStatusState.SUCCESS, 'Latest recovery point: {time_ago}', RelevantStatus.STAGING],
  syncFailed: [AppStatusState.ERROR, 'Sync to recovery environment failed', RelevantStatus.SYNC],
  testFailed: [AppStatusState.ERROR, 'Test failover to recovery environment failed', RelevantStatus.SYNC],
  testSuccess: [AppStatusState.SUCCESS, 'Test failover to recovery environment was successful', RelevantStatus.SYNC],
  cleaningUpDeletedAppResources: [
    AppStatusState.INDETERMINATE,
    'Cleaning up recovery environment resources',
    RelevantStatus.SYNC,
  ],
  deletedAppCleanupFailed: [AppStatusState.ERROR, 'Recovery environment clean up failed', RelevantStatus.SYNC],
} as { [key: string]: [AppStatusState, string, RelevantStatus] }

function formatRpStatusString(rpDate: Date): [AppStatusState, string, RelevantStatus] {
  // If we don't have a recovery point date, we presume that information will be
  // available shortly and put an ellipsis in its place.
  const taString = rpDate ? timeAgoString(rpDate) : '…'
  return [
    appStatusVals.rpStatus[0],
    appStatusVals.rpStatus[1].replace('{time_ago}', taString),
    appStatusVals.rpStatus[2],
  ]
}

function formatApplyingRpString(rpDate: Date): [AppStatusState, string, RelevantStatus] {
  // If we don't have a recovery point date, we presume that information will be
  // available shortly and put an ellipsis in its place.
  const taString = rpDate ? timeAgoString(rpDate) : '…'
  return [
    appStatusVals.applyingRp[0],
    appStatusVals.applyingRp[1].replace('{time_ago}', taString),
    appStatusVals.applyingRp[2],
  ]
}

function wrapStatus(
  status: [AppStatusState, string, RelevantStatus],
  syncPairIssues: Array<RaisedIssue> = [],
  refreshIssues: Array<RaisedIssue> = []
): [AppStatusState, string, RelevantStatus, Array<RaisedIssue>] {
  const issues = [...syncPairIssues, ...refreshIssues]
  return [status[0], status[1], status[2], issues]
}

function computeStandbyStatusText(
  stagingStatus: ProcessStatus,
  syncStatus: ProcessStatus,
  appliedRpTimestamp: Date,
  stagingIssues: Array<RaisedIssue>,
  syncIssues: Array<RaisedIssue>,
  refreshIssues: Array<RaisedIssue>
) {
  switch (syncStatus) {
    case ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS:
      return wrapStatus(appStatusVals.initialSync)
    case ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS:
      return wrapStatus(formatApplyingRpString(appliedRpTimestamp))
    default:
      switch (stagingStatus) {
        case ProcessStatus.NEVER_ATTEMPTED:
          return wrapStatus(appStatusVals.awaitInitialRp)
        case ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS:
          return wrapStatus(appStatusVals.captureInitialRp)
        case ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS:
          return wrapStatus(appStatusVals.captureRp)
        case ProcessStatus.MOST_RECENT_FAILED:
          return wrapStatus(appStatusVals.captureRpFailed, stagingIssues, refreshIssues)
        case ProcessStatus.MOST_RECENT_SUCCEEDED:
          switch (syncStatus) {
            case ProcessStatus.NEVER_ATTEMPTED:
              return wrapStatus(appStatusVals.awaitInitialSync)
            case ProcessStatus.MOST_RECENT_FAILED:
              return wrapStatus(appStatusVals.syncFailed, syncIssues)
            case ProcessStatus.MOST_RECENT_SUCCEEDED:
              return wrapStatus(formatRpStatusString(appliedRpTimestamp))
          }
      }
  }

  return wrapStatus([
    AppStatusState.ERROR,
    `Unable to compute status text for SyncPhase: standby, ${stagingStatus}, ${syncStatus}.`,
    RelevantStatus.COMBINED,
  ])
}

function computeFailoverStatusText(
  stagingStatus: ProcessStatus,
  syncStatus: ProcessStatus,
  // tslint:disable-next-line:variable-name
  _stagingIssues: Array<RaisedIssue>,
  syncIssues: Array<RaisedIssue>
) {
  switch (stagingStatus) {
    case ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS:
    case ProcessStatus.MOST_RECENT_FAILED:
    case ProcessStatus.MOST_RECENT_SUCCEEDED:
      switch (syncStatus) {
        case ProcessStatus.NEVER_ATTEMPTED:
          return wrapStatus(appStatusVals.awaitFailover)
        case ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS:
          return wrapStatus(appStatusVals.failingOver)
        case ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS:
          return wrapStatus(appStatusVals.failingOver)
        case ProcessStatus.MOST_RECENT_FAILED:
          return wrapStatus(appStatusVals.failoverFailed, syncIssues)
        case ProcessStatus.MOST_RECENT_SUCCEEDED:
          return wrapStatus(appStatusVals.failedOver)
      }
      break
  }
  return wrapStatus([
    AppStatusState.ERROR,
    `Unable to compute status text for SyncPhase: failover, ${stagingStatus}, ${syncStatus}.`,
    RelevantStatus.SYNC,
  ])
}

function computeFailoverTestStatusText(
  stagingStatus: ProcessStatus,
  syncStatus: ProcessStatus,
  // tslint:disable-next-line:variable-name
  _stagingIssues: Array<RaisedIssue>,
  syncIssues: Array<RaisedIssue>
) {
  switch (stagingStatus) {
    case ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS:
    case ProcessStatus.MOST_RECENT_FAILED:
    case ProcessStatus.MOST_RECENT_SUCCEEDED:
      switch (syncStatus) {
        case ProcessStatus.NEVER_ATTEMPTED:
          return wrapStatus(appStatusVals.awaitTest)
        case ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS:
          return wrapStatus(appStatusVals.testing)
        case ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS:
          return wrapStatus(appStatusVals.testing)
        case ProcessStatus.MOST_RECENT_FAILED:
          return wrapStatus(appStatusVals.testFailed, syncIssues)
        case ProcessStatus.MOST_RECENT_SUCCEEDED:
          return wrapStatus(appStatusVals.testSuccess)
      }
      break
  }
  return wrapStatus([
    AppStatusState.ERROR,
    `Unable to compute status text for SyncPhase: failoverTest, ${stagingStatus}, ${syncStatus}.`,
    RelevantStatus.SYNC,
  ])
}

export function computeStatusText(
  syncPhase: SyncPhaseDto,
  stagingStatus: ProcessStatus,
  syncStatus: ProcessStatus,
  appliedRpTimestamp: Date,
  syncIssues: Array<RaisedIssue>,
  stagingIssues: Array<RaisedIssue>,
  refreshIssues: Array<RaisedIssue>
): [AppStatusState, string, RelevantStatus, Array<RaisedIssue>] {
  switch (syncPhase) {
    case SyncPhaseDto.STANDBY:
      return computeStandbyStatusText(
        stagingStatus,
        syncStatus,
        appliedRpTimestamp,
        syncIssues,
        stagingIssues,
        refreshIssues
      )
    case SyncPhaseDto.FAILOVER:
      return computeFailoverStatusText(stagingStatus, syncStatus, syncIssues, stagingIssues)
    case SyncPhaseDto.FAILOVER_TEST:
      return computeFailoverTestStatusText(stagingStatus, syncStatus, syncIssues, stagingIssues)
  }

  return wrapStatus(
    [
      AppStatusState.ERROR,
      `Unable to compute status text for ${syncPhase}, ${stagingStatus}, ${syncStatus}.`,
      RelevantStatus.COMBINED,
    ],
    stagingIssues.concat(syncIssues)
  )
}
